import React, { useState } from "react";
import {
  Box,
  ButtonBase,
  Divider,
  Tooltip,
  IconButton,
  Popover,
} from "@mui/material";
import { motion, LayoutGroup } from "framer-motion";
import TableChartIcon from '@mui/icons-material/TableChart';

// ─── Brand tokens ──────────────────────────────────────────────────────────────
const C = {
  aqua:        "#63d7d5",
  aquaDark:    "#0f9fab",
  aquaSoft:    "#e8fbfb",
  ink:         "#0f172a",
  muted:       "#64748b",
  border:      "rgba(15,23,42,0.08)",
  surface:     "rgba(255,255,255,0.94)",
};

const SPRING = { type: "spring", stiffness: 320, damping: 32, mass: 1 };
const MotionButtonBase = motion(ButtonBase);

// ─── Dropdown nav item ─────────────────────────────────────────────────────────
function DropdownTab({ id, label, items, selectedSection, setThemeInUrl, isActive, openId, setOpenId, anchorEl, setAnchorEl, showActiveLabel = false }) {
  const open = openId === id;

  // When showActiveLabel is set, the button reflects the currently selected
  // item (e.g. ACC "Policy") instead of a fixed group label. Falls back to the
  // group label when nothing in this group is selected.
  const activeItem = items.find((o) => o.value === selectedSection);
  const displayLabel = showActiveLabel && activeItem ? activeItem.label : label;

  return (
    <>
      <MotionButtonBase
        disableRipple
        onClick={(e) => {
          if (open) { setOpenId(null); setAnchorEl(null); }
          else { setOpenId(id); setAnchorEl(e.currentTarget); }
        }}
        whileTap={{ scale: 0.96 }}
        sx={{
          display: "flex", alignItems: "center", gap: "5px",
          px: 2, py: 0.75, borderRadius: "8px",
          fontSize: "0.875rem",
          fontWeight: isActive ? 650 : 450,
          fontFamily: "'DM Sans', sans-serif",
          color: isActive ? C.aquaDark : C.muted,
          transition: "color 0.18s ease, background 0.18s ease",
          position: "relative",
          "&:hover": { color: isActive ? C.aquaDark : C.ink, bgcolor: "rgba(15,23,42,0.03)" },
        }}
      >
        <span>{displayLabel}</span>
        <motion.svg
          width="10" height="10" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"
          animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}
          style={{ opacity: 0.55 }}
        >
          <polyline points="6 9 12 15 18 9" />
        </motion.svg>
      </MotionButtonBase>

      <Popover
        open={open}
        anchorEl={open ? anchorEl : null}
        onClose={() => { setOpenId(null); setAnchorEl(null); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        disableScrollLock
        elevation={0}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 180,
              border: "1px solid rgba(15,23,42,0.09)",
              borderRadius: "12px",
              boxShadow: "0 12px 40px rgba(15,23,42,0.12), 0 2px 8px rgba(15,23,42,0.06)",
              bgcolor: "#fff",
              overflow: "hidden",
              py: 0.75,
            },
          },
        }}
      >
        {/* Active teal left-border indicator per row */}
        {items.map((opt) => {
          const isSel = selectedSection === opt.value;
          return (
            <ButtonBase
              key={opt.value}
              disableRipple
              onClick={() => { setThemeInUrl(opt.value); setOpenId(null); }}
              sx={{
                display: "flex", alignItems: "center",
                width: "100%", textAlign: "left",
                pl: isSel ? 1.75 : 2, pr: 2, py: 1,
                gap: 1.25,
                fontSize: "0.855rem",
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: isSel ? 650 : 430,
                color: isSel ? C.aquaDark : "#1e293b",
                bgcolor: isSel ? C.aquaSoft : "transparent",
                borderLeft: isSel ? `3px solid ${C.aquaDark}` : "3px solid transparent",
                transition: "all 0.13s ease",
                "&:hover": {
                  bgcolor: C.aquaSoft,
                  color: C.aquaDark,
                  borderLeftColor: C.aqua,
                },
              }}
            >
              {opt.label}
            </ButtonBase>
          );
        })}
      </Popover>
    </>
  );
}

// ─── Single flat tab (for client tab) ─────────────────────────────────────────
function FlatTab({ label, value, selectedSection, setThemeInUrl }) {
  const isSelected = selectedSection === value;
  return (
    <MotionButtonBase
      disableRipple
      onClick={() => setThemeInUrl(value)}
      whileTap={{ scale: 0.96 }}
      sx={{
        display: "flex", alignItems: "center", gap: "6px",
        px: 2, py: 0.75, borderRadius: "8px",
        fontSize: "0.875rem",
        fontWeight: isSelected ? 650 : 450,
        fontFamily: "'DM Sans', sans-serif",
        color: isSelected ? C.aquaDark : C.muted,
        transition: "color 0.18s ease, background 0.18s ease",
        position: "relative",
        "&:hover": { color: isSelected ? C.aquaDark : C.ink, bgcolor: isSelected ? "transparent" : "rgba(15,23,42,0.03)" },
      }}
    >
      <span>{label}</span>
      {isSelected && (
        <Box
          component={motion.div}
          layoutId="active-underline"
          transition={SPRING}
          sx={{ position: "absolute", bottom: 0, left: 8, right: 8, height: "2px", borderRadius: "2px", bgcolor: C.aquaDark, opacity: 0.7 }}
        />
      )}
    </MotionButtonBase>
  );
}

export default function Header({
  visibleThemes,
  selectedSection,
  clientAccess,
  setThemeInUrl,
  UserMenu,
  user,
  onLogout,
  onTableauOpen,
  showReportTab = false,
  reportActive = false,
  onReportOpen,
  onTourRestart,
}) {
  const showTableauButton = clientAccess === "acc";
  const [openId, setOpenId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const pulseItems   = visibleThemes.filter(t => t.group === "pulse");
  const monitorItems = visibleThemes.filter(t => t.group === "monitor");
  const clientItems  = visibleThemes.filter(t => t.group === "client");

  const pulseActive   = pulseItems.some(t => t.value === selectedSection);
  const monitorActive = monitorItems.some(t => t.value === selectedSection);

  // Client tab: use first client item's label, or a generic name
  const clientItem = clientItems[0] || null;
  const clientLabel = clientItem?.label || "Client";

  return (
    <Box
      component={motion.header}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 100, damping: 20 }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        height: 58,
        position: "sticky",
        top: 0,
        zIndex: 1500,
        backgroundColor: C.surface,
        backdropFilter: "blur(20px) saturate(180%)",
        WebkitBackdropFilter: "blur(20px) saturate(180%)",
        borderBottom: `1px solid ${C.border}`,
        boxShadow: "0 1px 0 rgba(15,23,42,0.04)",
      }}
    >

      {/* ── Left: Logo + client badges + tour button ── */}
      <Box sx={{ display: "flex", alignItems: "center", minWidth: 180, gap: 1 }}>
        <motion.img
          src={process.env.PUBLIC_URL + "/static/images/districtprofiles2.png"}
          alt="District Profiles"
          style={{ height: 40, width: "auto", objectFit: "contain" }}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.95 }}
          transition={{ duration: 0.2 }}
        />

        {clientAccess === "invariant" && (
          <>
            <motion.img
              src={process.env.PUBLIC_URL + "/static/images/invariant.svg"}
              alt="Invariant"
              style={{ height: 28, width: "auto", objectFit: "contain" }}
              transition={{ duration: 0.2 }}
            />
            <motion.img
              src={process.env.PUBLIC_URL + "/static/images/pcpc.png"}
              alt="PCPC"
              style={{ height: 24, width: "auto", objectFit: "contain" }}
              transition={{ duration: 0.2 }}
            />
          </>
        )}

        {clientAccess === "intuit" && (
          <Box sx={{
            px: 1.25, py: 0.3, borderRadius: 999,
            fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.06em",
            textTransform: "uppercase", color: "#0f5132",
            backgroundColor: "#d1f2df", border: "1px solid #9be2bf",
          }}>
            Demo
          </Box>
        )}

        {clientAccess === "demo" && (
          <Box sx={{
            px: 1.25, py: 0.3, borderRadius: 999,
            fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.06em",
            textTransform: "uppercase", color: "#92400e",
            backgroundColor: "#fff3cd", border: "1px solid #f3d38a",
          }}>
            Demo Version
          </Box>
        )}

        {onTourRestart && (
          <Tooltip title="Replay guided tour" placement="bottom">
            <IconButton
              component={motion.button}
              onClick={onTourRestart}
              whileHover={{ scale: 1.15, rotate: 15 }}
              whileTap={{ scale: 0.9 }}
              transition={{ type: "spring", stiffness: 400, damping: 15 }}
              size="small"
              sx={{
                width: 26, height: 26, ml: 0.5,
                color: "rgba(0,0,0,0.3)",
                border: `1px solid ${C.border}`,
                bgcolor: "transparent",
                "&:hover": { color: C.aquaDark, borderColor: "rgba(15,159,171,0.3)", bgcolor: C.aquaSoft },
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>
              </svg>
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* ── Centre: Grouped nav ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
        <LayoutGroup id="district-tabs">

          {pulseItems.length > 0 && (
            pulseItems.length === 1
              ? <FlatTab label={pulseItems[0].label} value={pulseItems[0].value} selectedSection={selectedSection} setThemeInUrl={setThemeInUrl} />
              : <DropdownTab id="pulse" label="Tracking Questions" items={pulseItems} selectedSection={selectedSection} setThemeInUrl={setThemeInUrl} isActive={pulseActive} openId={openId} setOpenId={setOpenId} anchorEl={anchorEl} setAnchorEl={setAnchorEl} />
          )}

          {monitorItems.length > 0 && (
            monitorItems.length === 1
              ? <FlatTab label="Issue snapshot" value={monitorItems[0].value} selectedSection={selectedSection} setThemeInUrl={setThemeInUrl} />
              : <DropdownTab id="monitor" label="Issue snapshot" items={monitorItems} selectedSection={selectedSection} setThemeInUrl={setThemeInUrl} isActive={monitorActive} openId={openId} setOpenId={setOpenId} anchorEl={anchorEl} setAnchorEl={setAnchorEl} />
          )}

          {clientItems.length === 1 && (
            <>
              <Divider orientation="vertical" flexItem sx={{ height: 20, alignSelf: "center", mx: 0.5, opacity: 0.3 }} />
              <FlatTab label={clientLabel} value={clientItem.value} selectedSection={selectedSection} setThemeInUrl={setThemeInUrl} />
            </>
          )}

          {clientItems.length > 1 && (
            <>
              <Divider orientation="vertical" flexItem sx={{ height: 20, alignSelf: "center", mx: 0.5, opacity: 0.3 }} />
              <DropdownTab
                id="client"
                label={clientLabel}
                items={clientItems}
                selectedSection={selectedSection}
                setThemeInUrl={setThemeInUrl}
                isActive={clientItems.some(t => t.value === selectedSection)}
                openId={openId}
                setOpenId={setOpenId}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                showActiveLabel
              />
            </>
          )}

        </LayoutGroup>
      </Box>

      {/* ── Right: Build Report + Tableau + Avatar ── */}
      <Box sx={{ display: "flex", alignItems: "center", minWidth: 180, justifyContent: "flex-end", gap: 1 }}>

        {showReportTab && (
          <>
            <Divider orientation="vertical" flexItem sx={{ height: 22, alignSelf: "center", opacity: 0.35 }} />
            <MotionButtonBase
              id="tour-report-tab"
              disableRipple
              onClick={onReportOpen}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              sx={{
                display: "flex", alignItems: "center", gap: "6px",
                px: 1.75, py: 0.7, borderRadius: "8px",
                fontSize: "0.82rem", fontWeight: 600, letterSpacing: "0.01em",
                fontFamily: "'DM Sans', sans-serif",
                color: reportActive ? C.aquaDark : C.muted,
                backgroundColor: reportActive ? C.aquaSoft : "transparent",
                border: `1px solid ${reportActive ? "rgba(15,159,171,0.28)" : C.border}`,
                transition: "all 0.18s ease",
                "&:hover": { color: C.aquaDark, backgroundColor: C.aquaSoft, borderColor: "rgba(15,159,171,0.28)" },
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
              Build Report
            </MotionButtonBase>
          </>
        )}

        {showTableauButton && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
            <Tooltip title="View Competitiveness Matrix">
              <IconButton
                onClick={onTableauOpen}
                size="small"
                sx={{
                  color: C.muted,
                  border: `1px solid ${C.border}`,
                  "&:hover": { color: C.aquaDark, bgcolor: C.aquaSoft, borderColor: "rgba(15,159,171,0.28)" },
                }}
              >
                <TableChartIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </motion.div>
        )}

        <Divider orientation="vertical" flexItem sx={{ height: 22, alignSelf: "center", opacity: 0.35 }} />

        <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.95 }} style={{ display: "flex" }}>
          <UserMenu user={user} onLogout={onLogout} />
        </motion.div>
      </Box>
    </Box>
  );
}
